import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/RegisterOwner.css';

const RegisterOwner = ({ handleSubmit: externalHandleSubmit, handleChange: externalHandleChange, error: externalError, success: externalSuccess, formData: externalFormData, fieldErrors: externalFieldErrors }) => {
    const { t } = useTranslation('auth');
    const [internalFormData, setInternalFormData] = useState({
        password: '', firstName: '', lastName: '',
        phone: '', country: '', city: '', postalCode: '',
        street: '', buildingNumber: '', apartmentNumber: '',
        statuteAccepted: false
    });

    const [internalError, setInternalError] = useState('');
    const [internalSuccess, setInternalSuccess] = useState('');
    const [internalFieldErrors, setInternalFieldErrors] = useState({});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Use external props if provided (for RegisterInvitedTrainer), otherwise use internal state
    const formData = externalFormData || internalFormData;
    const setFormData = externalFormData ? () => {} : setInternalFormData;
    const error = externalError || internalError;
    const success = externalSuccess || internalSuccess;
    const fieldErrors = externalFieldErrors || internalFieldErrors;

    const handleChange = externalHandleChange || ((e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
        // Clear field error when user starts typing
        if (internalFieldErrors[e.target.name]) {
            setInternalFieldErrors({ ...internalFieldErrors, [e.target.name]: '' });
        }
    });

    const handleSubmit = externalHandleSubmit || (async (e) => {
        e.preventDefault();
        setInternalError('');
        setInternalSuccess('');
        setInternalFieldErrors({});

        if (!token) {
            setInternalError(t('register.invalidToken'));
            return;
        }

        const requestBody = {
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            statuteAccepted: formData.statuteAccepted,
            address: {
                country: formData.country,
                city: formData.city,
                postalCode: formData.postalCode,
                street: formData.street,
                buildingNumber: formData.buildingNumber,
                apartmentNumber: formData.apartmentNumber
            }
        };

        try {
            await axios.post(`/api/v1/auth/register/owner?token=${token}`, requestBody);
            setInternalSuccess(t('register.ownerSuccess'));
            setTimeout(() => navigate('/'), 2000);
        } catch (err) {
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const mappedErrors = {};

                Object.keys(errors).forEach(key => {
                    if (key.startsWith('address.')) {
                        const fieldName = key.replace('address.', '');
                        mappedErrors[fieldName] = errors[key];
                    } else {
                        mappedErrors[key] = errors[key];
                    }
                });

                setInternalFieldErrors(mappedErrors);
                setInternalError(err.response.data.message || t('register.validation.validationError'));
            } else {
                setInternalError(t('register.registrationFailed'));
            }
        }
    });

    const handleBack = () => {
        navigate(-1);
    };

    if (!token && !externalHandleSubmit) {
        return (
            <div className="owner-registration">
                <div className="owner-registration__left-panel">
                    <div className="owner-registration__left-content">
                        <h2 className="owner-registration__welcome-text">
                            {t('register.ownerWelcome.welcomeIn')}
                            <span className="owner-registration__welcome-text--highlight">{t('register.ownerWelcome.ourTeam')}</span>
                        </h2>
                    </div>
                </div>
                <div className="owner-registration__right-panel">
                    <div className="owner-registration__form-wrapper">
                        <h1 className="owner-registration__title">Error</h1>
                        <p className="owner-registration__error">{t('register.invalidToken')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="owner-registration">
            <div className="owner-registration__left-panel">
                <div className="owner-registration__left-content">
                    <h2 className="owner-registration__welcome-text">
                        {t('register.ownerWelcome.welcomeIn')}
                        <span className="owner-registration__welcome-text--highlight">{t('register.ownerWelcome.ourTeam')}</span>
                    </h2>
                </div>
            </div>
            <div className="owner-registration__right-panel">
                <button className="owner-registration__back-btn" onClick={handleBack}>
                    {t('register.back')}
                </button>
                <div className="owner-registration__form-wrapper">
                    <h1 className="owner-registration__title">{t('register.ownerTitle')}</h1>
                    <form className="owner-registration__form" onSubmit={handleSubmit}>
                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="password"
                                    name="password"
                                    placeholder={t('register.password')}
                                    className={`owner-registration__input ${fieldErrors.password ? 'owner-registration__input--error' : ''}`}
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.password && <span className="owner-registration__field-error">{fieldErrors.password}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder={t('register.firstName')}
                                    className={`owner-registration__input ${fieldErrors.firstName ? 'owner-registration__input--error' : ''}`}
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.firstName && <span className="owner-registration__field-error">{fieldErrors.firstName}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder={t('register.lastName')}
                                    className={`owner-registration__input ${fieldErrors.lastName ? 'owner-registration__input--error' : ''}`}
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.lastName && <span className="owner-registration__field-error">{fieldErrors.lastName}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder={t('register.phone')}
                                    className={`owner-registration__input ${fieldErrors.phone ? 'owner-registration__input--error' : ''}`}
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.phone && <span className="owner-registration__field-error">{fieldErrors.phone}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="country"
                                    placeholder={t('register.country')}
                                    className={`owner-registration__input ${fieldErrors.country ? 'owner-registration__input--error' : ''}`}
                                    value={formData.country}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.country && <span className="owner-registration__field-error">{fieldErrors.country}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder={t('register.city')}
                                    className={`owner-registration__input ${fieldErrors.city ? 'owner-registration__input--error' : ''}`}
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.city && <span className="owner-registration__field-error">{fieldErrors.city}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder={t('register.postalCode')}
                                    className={`owner-registration__input ${fieldErrors.postalCode ? 'owner-registration__input--error' : ''}`}
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.postalCode && <span className="owner-registration__field-error">{fieldErrors.postalCode}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="street"
                                    placeholder={t('register.street')}
                                    className={`owner-registration__input ${fieldErrors.street ? 'owner-registration__input--error' : ''}`}
                                    value={formData.street}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.street && <span className="owner-registration__field-error">{fieldErrors.street}</span>}
                            </div>
                        </div>

                        <div className="owner-registration__form-row">
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="buildingNumber"
                                    placeholder={t('register.buildingNumber')}
                                    className={`owner-registration__input ${fieldErrors.buildingNumber ? 'owner-registration__input--error' : ''}`}
                                    value={formData.buildingNumber}
                                    onChange={handleChange}
                                    required
                                />
                                {fieldErrors.buildingNumber && <span className="owner-registration__field-error">{fieldErrors.buildingNumber}</span>}
                            </div>
                            <div className="owner-registration__field-group">
                                <input
                                    type="text"
                                    name="apartmentNumber"
                                    placeholder={t('register.apartmentNumber')}
                                    className="owner-registration__input"
                                    value={formData.apartmentNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="owner-registration__checkbox-group">
                            <label className="owner-registration__checkbox-label">
                                <input
                                    type="checkbox"
                                    name="statuteAccepted"
                                    checked={formData.statuteAccepted}
                                    onChange={handleChange}
                                    required
                                    className="owner-registration__checkbox"
                                />
                                <span>{t('register.acceptTermsWithLink')}</span>
                            </label>
                            {fieldErrors.statuteAccepted && <span className="owner-registration__field-error">{fieldErrors.statuteAccepted}</span>}
                        </div>

                        {error && <div className="owner-registration__error">{error}</div>}
                        {success && <div className="owner-registration__success">{success}</div>}

                        <button type="submit" className="owner-registration__submit-btn">
                            {t('register.registerButton')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterOwner;
